import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect text overflow in truncated elements
 *
 * Uses ResizeObserver to monitor element size changes and detect when
 * text content exceeds the element's visible width. This is used to
 * automatically show tooltips on truncated text.
 *
 * @param text - The text content to monitor for overflow
 * @returns Object with ref to attach to element and hasOverflow boolean
 *
 * @example
 * ```tsx
 * const { ref, hasOverflow } = useHasTextOverflow("Long text content...");
 *
 * <span ref={ref} className="truncate">
 *   Long text content...
 * </span>
 * {hasOverflow && <Tooltip>Long text content...</Tooltip>}
 * ```
 */
const useHasTextOverflow = (text: string | React.ReactNode) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkOverflow = () => {
      // Element is overflowing if scrollWidth exceeds visible clientWidth
      const isOverflowing = element.scrollWidth > element.clientWidth;
      setHasOverflow(isOverflowing);
    };

    // Use ResizeObserver to detect size changes (window resize, content changes)
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    // Initial check
    checkOverflow();

    // Cleanup observer on unmount
    return () => resizeObserver.disconnect();
  }, [text]); // Re-run when text changes

  return { ref, hasOverflow };
};

export default useHasTextOverflow;
